import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams, NavLink } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import config from '../config';
import auth from '../helper/auth';
import MasterLayout from '../masterLayout/MasterLayout';
import Breadcrumb from '../components/Breadcrumb';
import { formatDisplayDate } from '../utils/formatDate';

const TosPage = () => {
    const { logout } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [content, setContent] = useState('');
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [canAccept, setCanAccept] = useState(false);
    const [accepting, setAccepting] = useState(false);
    const [showDeclineModal, setShowDeclineModal] = useState(false);
    
    const sentinelRef = useRef(null);
    const isGuardMode = searchParams.get('mode') === 'guard';

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [contentRes, statusRes] = await Promise.all([
                    fetch(`${config.API_BASE_URL}/api/tos/content`),
                    auth.fetch(`${config.API_BASE_URL}/api/tos/status`)
                ]);
                
                const contentData = await contentRes.json();
                const statusData = await statusRes.json();
                
                setContent(contentData.content);
                setStatus(statusData);
                
                if (!statusData.requiresAcceptance && isGuardMode) {
                    navigate('/applications', { replace: true });
                }
            } catch (err) {
                console.error('Failed to fetch ToS data:', err);
                toast.error('Failed to load Terms of Service');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [navigate, isGuardMode]);

    useEffect(() => {
        if (!content || !status?.requiresAcceptance) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setCanAccept(true);
                }
            },
            { threshold: 0.5 }
        );

        if (sentinelRef.current) {
            observer.observe(sentinelRef.current);
        }

        return () => observer.disconnect();
    }, [content, status]);

    const handleAccept = async () => {
        if (!canAccept || accepting) return;
        setAccepting(true);
        try {
            const res = await auth.fetch(`${config.API_BASE_URL}/api/tos/accept`, {
                method: 'POST',
                body: JSON.stringify({ version: status.version })
            });
            
            if (res.success) {
                toast.success('Terms of Service accepted');
                window.location.href = '/applications';
            } else {
                throw new Error(res.error || 'Failed to accept ToS');
            }
        } catch (err) {
            console.error(err);
            toast.error(err.message);
        } finally {
            setAccepting(false);
        }
    };

    const handleDecline = () => {
        setShowDeclineModal(true);
    };

    const handleLogout = () => {
        logout();
        navigate('/sign-in');
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    const PageContent = (
        <div className="container-fluid py-2">
            <style>{`
                .tos-content h1,
                .tos-content h2,
                .tos-content h3,
                .tos-content h4,
                .tos-content h5,
                .tos-content .section-title,
                .tos-content [style*="border"] {
                    border: none !important;
                    border-left: none !important;
                    background: transparent !important;
                    box-shadow: none !important;
                }
                .tos-content h1,
                .tos-content h2,
                .tos-content h3,
                .tos-content h4 {
                    font-weight: 700 !important;
                    color: var(--hs-text-primary, #0f172a) !important;
                    padding: 0 0 8px 0 !important;
                    margin-top: 24px !important;
                    margin-bottom: 12px !important;
                    border-bottom: 1px solid var(--hs-border, #e2e8f0) !important;
                }
                [data-theme='dark'] .tos-content,
                [data-theme='dark'] .tos-content p,
                [data-theme='dark'] .tos-content li,
                [data-theme='dark'] .tos-content span {
                    color: var(--hs-text-secondary) !important;
                }
            `}</style>
            <div className="row justify-content-center">
                <div className="col-12 col-xl-10">
                    <div
                        className="card border radius-16 overflow-hidden d-flex flex-column bg-white"
                        style={{ height: 'calc(100vh - 175px)', minHeight: '520px' }}
                    >
                        {/* Fixed Top Bar */}
                        <div className="card-header bg-white py-16 px-24 d-flex justify-content-between align-items-center border-bottom flex-shrink-0">
                            <h6 className="mb-0 fw-bold text-dark">HubScroll Business Terms of Service</h6>
                            {status?.accepted_at && !status?.requiresAcceptance && (
                                <span className="hs-badge hs-badge-success">
                                    Accepted {formatDisplayDate(status.accepted_at)}
                                </span>
                            )}
                        </div>

                        {/* Scrollable Document Body */}
                        <div className="card-body p-0 bg-neutral-50 flex-grow-1" style={{ overflowY: 'auto' }}>
                            <div 
                                className="mx-auto my-24 p-24 p-md-40 bg-white border radius-12" 
                                style={{ maxWidth: '780px', minHeight: '100%' }}
                            >
                                <div className="tos-content" dangerouslySetInnerHTML={{ __html: content }} />
                                <div ref={sentinelRef} style={{ height: '4px' }} />
                            </div>
                        </div>

                        {/* Fixed Bottom Bar */}
                        <div className="card-footer bg-white py-16 px-24 border-top flex-shrink-0 d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
                            <div className="text-secondary-light text-sm">
                                Please review the full Terms of Service before accepting.
                            </div>
                            <div className="d-flex gap-2 w-100 w-md-auto justify-content-end">
                                <button 
                                    type="button"
                                    className="hs-btn-secondary text-danger-600 px-24" 
                                    onClick={handleDecline}
                                >
                                    Decline
                                </button>
                                <button 
                                    type="button"
                                    className="hs-btn-primary px-24" 
                                    disabled={(status?.requiresAcceptance && !canAccept) || accepting}
                                    onClick={handleAccept}
                                >
                                    {accepting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Accepting...
                                        </>
                                    ) : 'I accept the Terms of Service \u2713'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Decline Modal */}
            {showDeclineModal && (
                <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(15, 23, 42, 0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content radius-16 border-0 shadow">
                            <div className="modal-header border-bottom py-16 px-24">
                                <h6 className="modal-title fw-bold text-dark mb-0">Action Required</h6>
                                <button type="button" className="btn-close" onClick={() => setShowDeclineModal(false)} />
                            </div>
                            <div className="modal-body p-24 text-center">
                                <p className="mb-0 text-dark text-sm">You must accept the Terms of Service to use the HubScroll platform. Declining will log you out of your account.</p>
                            </div>
                            <div className="modal-footer border-top py-16 px-24 justify-content-end gap-2">
                                <button type="button" className="hs-btn-secondary px-20" onClick={() => setShowDeclineModal(false)}>Cancel</button>
                                <button type="button" className="hs-btn-primary bg-danger-600 border-danger-600 px-20" onClick={handleLogout}>Log Out</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    if (isGuardMode) {
        return (
            <div className="bg-light min-vh-100 py-4">
                <div className="container-fluid">
                    <div className="row justify-content-center">
                        <div className="col-12 col-xl-10 mb-12">
                            <Breadcrumb title="Terms of Service" />
                        </div>
                    </div>
                </div>
                {PageContent}
            </div>
        );
    }

    return (
        <MasterLayout>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
                <Breadcrumb title="Terms of Service" />
                <div className="d-flex align-items-center gap-2 p-1 bg-neutral-100 radius-8">
                    <NavLink
                        to="/tos"
                        className={({ isActive }) => `btn btn-sm px-16 py-6 radius-6 fw-semibold d-inline-flex align-items-center gap-1 transition-2 ${isActive ? 'btn-primary text-white shadow-xs' : 'btn-outline-neutral text-secondary-light border-0'}`}
                    >
                        <Icon icon="solar:document-text-bold" className="text-base" />
                        <span>Terms of Service</span>
                    </NavLink>
                    <NavLink
                        to="/privacy"
                        className={({ isActive }) => `btn btn-sm px-16 py-6 radius-6 fw-semibold d-inline-flex align-items-center gap-1 transition-2 ${isActive ? 'btn-primary text-white shadow-xs' : 'btn-outline-neutral text-secondary-light border-0'}`}
                    >
                        <Icon icon="solar:shield-check-linear" className="text-base" />
                        <span>Privacy Policy</span>
                    </NavLink>
                </div>
            </div>
            {PageContent}
        </MasterLayout>
    );
};

export default TosPage;
